import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/seo/SEOHead";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Privacy Policy | LaunchGuide.io"
        description="How LaunchGuide B.V. collects, uses, and protects personal data when you use launchguide.io and the LaunchGuide service."
        pathname="/privacy-policy"
      />
      <Navbar />
      
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-8 text-center">Privacy Policy</h1>
          <p className="text-lg text-muted-foreground mb-8 text-center">
            Last updated: 2 June 2025
          </p>

          <div className="prose prose-lg max-w-none dark:prose-invert">
            {/* Section 1 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">1. Who We Are</h2>
            <p className="mb-4 leading-relaxed">
              LaunchGuide B.V. ("LaunchGuide", "we", "us" or "our") is a private limited company registered in the Netherlands. LaunchGuide is the <strong>controller</strong> of the personal data described in this Policy.
            </p>
            <p className="mb-6 leading-relaxed">
              You can reach our privacy team at <strong><a href="mailto:hello@launchguide.io" className="text-blue-600 dark:text-blue-400 hover:underline">hello@launchguide.io</a></strong>.
            </p>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">2. Scope of This Policy</h2>
            <p className="mb-4 leading-relaxed">This Privacy Policy applies to personal data processed when you:</p>
            <ul className="mb-6 list-disc pl-6 space-y-1">
              <li>visit or interact with our website <strong>launchguide.io</strong> and any sub‑domains;</li>
              <li>create or use an account on the LaunchGuide platform (the "Service");</li>
              <li>communicate with us via email, chat or social media;</li>
              <li>receive marketing communications from us.</li>
            </ul>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">3. Personal Data We Collect</h2>
            <div className="overflow-x-auto mb-6 rounded-lg border border-zinc-200 dark:border-zinc-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 dark:bg-zinc-800">
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Category</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Examples</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Account data</td>
                    <td className="px-4 py-3">name, email address, password (hashed), company name, role, avatar</td>
                    <td className="px-4 py-3">Provided by you</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30 border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Usage data</td>
                    <td className="px-4 py-3">IP address, browser & device information, timestamps, pages viewed, clicks, referral URL</td>
                    <td className="px-4 py-3">Collected automatically</td>
                  </tr>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Platform content</td>
                    <td className="px-4 py-3">text, files, images and other materials you upload or generate through the Service</td>
                    <td className="px-4 py-3">Provided by you</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30 border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Payment data</td>
                    <td className="px-4 py-3">last four digits of card, billing country, subscription details (processed by Stripe)</td>
                    <td className="px-4 py-3">Provided by you / Stripe</td>
                  </tr>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Support data</td>
                    <td className="px-4 py-3">correspondence, bug reports, feedback</td>
                    <td className="px-4 py-3">Provided by you</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30">
                    <td className="px-4 py-3 font-medium">Third–party data</td>
                    <td className="px-4 py-3">OAuth profile data (Google/GitHub), public company info from our website crawler</td>
                    <td className="px-4 py-3">Third parties</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">4. Purposes and Legal Bases</h2>
            <div className="overflow-x-auto mb-6 rounded-lg border border-zinc-200 dark:border-zinc-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 dark:bg-zinc-800">
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Purpose</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Legal basis (Art. 6 GDPR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3">Create and administer your account; provide the Service</td>
                    <td className="px-4 py-3">Contract performance Art. 6(1)(b)</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30 border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3">Process payments and invoices</td>
                    <td className="px-4 py-3">Contract performance Art. 6(1)(b); Legal obligation Art. 6(1)(c)</td>
                  </tr>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3">Provide customer support</td>
                    <td className="px-4 py-3">Legitimate interest Art. 6(1)(f)</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30 border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3">Improve, secure and debug the Service; analytics</td>
                    <td className="px-4 py-3">Legitimate interest Art. 6(1)(f)</td>
                  </tr>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3">Send product updates and newsletters</td>
                    <td className="px-4 py-3">Consent Art. 6(1)(a) or Legitimate interest Art. 6(1)(f) (B2B soft opt‑in)</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30">
                    <td className="px-4 py-3">Comply with legal obligations (tax, accounting, law‑enforcement requests)</td>
                    <td className="px-4 py-3">Legal obligation Art. 6(1)(c)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mb-6 leading-relaxed">
              If we rely on your <strong>consent</strong>, you may withdraw it at any time without affecting the lawfulness of processing before withdrawal.
            </p>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">5. Cookies and Similar Technologies</h2>
            <p className="mb-4 leading-relaxed">We use:</p>
            <ul className="mb-4 list-disc pl-6 space-y-1">
              <li><strong>Essential cookies</strong> – required for site functionality and security;</li>
              <li><strong>Analytics cookies</strong> – help us understand how the Service is used;</li>
              <li><strong>Marketing cookies</strong> – allow us to deliver relevant content.</li>
            </ul>
            <p className="mb-6 leading-relaxed">
              You can manage cookie preferences via our banner or your browser settings. For more detail please see our [Cookie Policy].
            </p>

            {/* Section 6 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">6. How Long We Keep Your Data</h2>
            <p className="mb-4 leading-relaxed">We keep personal data only as long as necessary:</p>
            <ul className="mb-6 list-disc pl-6 space-y-1">
              <li>Account data – for the lifetime of your account and up to 12 months after closure;</li>
              <li>Financial records – 7 years (statutory requirement);</li>
              <li>Support tickets – 24 months;</li>
              <li>Back‑ups – retained securely for 30 days before automatic deletion.</li>
            </ul>

            {/* Section 7 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">7. Sharing and Disclosure</h2>
            <p className="mb-4 leading-relaxed">We never sell your data. We share it only with:</p>
            <div className="overflow-x-auto mb-6 rounded-lg border border-zinc-200 dark:border-zinc-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 dark:bg-zinc-800">
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Recipient</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Role</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-zinc-200 dark:border-zinc-700">Safeguards</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Netlify, Inc. (USA)</td>
                    <td className="px-4 py-3">Front‑end hosting & CDN</td>
                    <td className="px-4 py-3">Standard Contractual Clauses ("SCCs")</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30 border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Supabase Inc. (Ireland / USA)</td>
                    <td className="px-4 py-3">Managed database, authentication & storage</td>
                    <td className="px-4 py-3">SCCs; EU region option</td>
                  </tr>
                  <tr className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">OpenAI, L.L.C. (USA)</td>
                    <td className="px-4 py-3">AI content generation</td>
                    <td className="px-4 py-3">SCCs + data residence controls</td>
                  </tr>
                  <tr className="bg-zinc-25 dark:bg-zinc-900/30 border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-medium">Stripe Payments Europe Ltd. (Ireland)</td>
                    <td className="px-4 py-3">Payment processing</td>
                    <td className="px-4 py-3">GDPR‑compliant DPA</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Competent public authorities</td>
                    <td className="px-4 py-3">Legal compliance</td>
                    <td className="px-4 py-3">Legal obligation</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mb-6 leading-relaxed">
              All processors act under written Data Processing Agreements and only on our instructions.
            </p>

            {/* Section 8 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">8. International Transfers</h2>
            <p className="mb-4 leading-relaxed">
              Where personal data is transferred outside the European Economic Area ("EEA"), we rely on:
            </p>
            <ul className="mb-6 list-disc pl-6 space-y-1">
              <li>an adequacy decision of the European Commission; or</li>
              <li>Standard Contractual Clauses and supplementary safeguards such as encryption and strict access controls.</li>
            </ul>

            {/* Section 9 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">9. Security Measures</h2>
            <p className="mb-4 leading-relaxed">We implement appropriate technical and organisational measures including:</p>
            <ul className="mb-6 list-disc pl-6 space-y-1">
              <li>HTTPS/TLS encryption in transit and AES‑256 encryption at rest;</li>
              <li>Least‑privilege access controls and role‑based permissions;</li>
              <li>Regular penetration testing and vulnerability scanning;</li>
              <li>2‑factor authentication for staff and critical systems;</li>
              <li>Business continuity and incident response procedures.</li>
            </ul>

            {/* Section 10 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">10. Your Rights</h2>
            <p className="mb-4 leading-relaxed">
              Subject to conditions and limitations under the GDPR, you have the right to:
            </p>
            <ol className="mb-4 list-decimal pl-6 space-y-1">
              <li><strong>Access</strong> your personal data;</li>
              <li><strong>Rectify</strong> inaccurate or incomplete data;</li>
              <li><strong>Erase</strong> data ("right to be forgotten");</li>
              <li><strong>Restrict</strong> processing;</li>
              <li><strong>Object</strong> to processing based on legitimate interests or direct marketing;</li>
              <li><strong>Data portability</strong> (receive data in machine‑readable format);</li>
              <li><strong>Withdraw consent</strong> at any time;</li>
              <li><strong>Lodge a complaint</strong> with the Dutch Supervisory Authority (Autoriteit Persoonsgegevens) or your local authority.</li>
            </ol>
            <p className="mb-6 leading-relaxed">
              To exercise your rights, email <strong><a href="mailto:hello@launchguide.io" className="text-blue-600 dark:text-blue-400 hover:underline">hello@launchguide.io</a></strong>.
            </p>

            {/* Section 11 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">11. Automated Decision‑Making and Profiling</h2>
            <p className="mb-6 leading-relaxed">
              We do <strong>not</strong> use personal data for decisions producing legal or similarly significant effects based solely on automated processing.
            </p>

            {/* Section 12 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">12. Children</h2>
            <p className="mb-6 leading-relaxed">
              Our Service is not directed at children under 16. We do not knowingly process children's data. If you believe we have collected such data, please contact us immediately.
            </p>

            {/* Section 13 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">13. Changes to This Policy</h2>
            <p className="mb-6 leading-relaxed">
              We may amend this Policy from time to time. We will post the updated version on this page and, if changes are material, notify you via the Service or email. Please review this page periodically.
            </p>

            {/* Section 14 */}
            <h2 className="text-2xl font-bold mb-4 mt-8">14. Contact</h2>
            <p className="mb-4 leading-relaxed">
              If you have questions about this Policy or our privacy practices, contact us at:
            </p>
            <div className="mb-6 p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
              <p className="leading-relaxed">
                <strong>LaunchGuide</strong><br />
                Email: <a href="mailto:hello@launchguide.io" className="text-blue-600 dark:text-blue-400 hover:underline">hello@launchguide.io</a>
              </p>
            </div>

            {/* Footer */}
            <div className="border-t border-zinc-300 dark:border-zinc-600 pt-6 mt-8">
              <p className="text-center text-sm text-muted-foreground">
                Version 1.0 – Effective 2 June 2025
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;